import interfaceLogApi from '@/api/operations/interfaceLogApi'
import { queryKeys } from '@/constant/common'
import { InterfaceLog, LogOperator } from '@/types/interfaceLog'
import { LogTypeEnum } from '@/types/simulation'
import {
  IO_ERROR_LOG_FILTER_INDEX,
  StationErrorLogDetail,
  getLatestStationErrorLogs,
} from '@/utils/stationErrorLog'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

/** How far back to look for the io::on / io::off log that recorded the error. */
const LOOKBACK_DAYS = 30

const ERROR_LOG_TYPES = [LogTypeEnum.IoOn, LogTypeEnum.IoOff]

/** Picks up logs the controller uploads while the Error Summary is open. */
const REFETCH_INTERVAL_MS = 60_000

interface StationErrorRow {
  errorCode: number
}

/**
 * Latest errDetail for each error station, so the Error Summary can show what
 * happened without a manual Diagnose.
 *
 * One request per log type, filtered to rows with a non-zero errCode: errors
 * are rare, so this stays far below the BE's 10,000-row cap. Filtering by
 * decoder address with `In` is not used because not every environment
 * supports it.
 *
 * The BE sometimes answers 403 right after page load and then succeeds, so the
 * request is retried. The result is not cached past unmount: a failed refetch
 * would otherwise keep showing the previous, older timestamps.
 */
const useStationErrorLogs = (interfaceId: number, rows: StationErrorRow[]) => {
  const hasErrors = rows.some((row) => row.errorCode)

  return useQuery(
    [queryKeys.stationErrorLogs, interfaceId],
    async (): Promise<Map<string, StationErrorLogDetail>> => {
      const from = dayjs().subtract(LOOKBACK_DAYS, 'day').toISOString()
      const responses = await Promise.all(
        ERROR_LOG_TYPES.map((type) =>
          interfaceLogApi.getCrysbergLogs({
            interfaceId,
            logTypes: [type],
            dataFilterRules: [
              {
                index: IO_ERROR_LOG_FILTER_INDEX[type]!,
                operator: LogOperator.NotEqual,
                value: '0',
              },
            ],
            from,
          }),
        ),
      )
      const logs: InterfaceLog[] = responses.flatMap((res: any) =>
        Array.isArray(res) ? res : (res?.data ?? []),
      )
      return getLatestStationErrorLogs(logs)
    },
    {
      enabled: interfaceId > 0 && hasErrors,
      retry: 3,
      cacheTime: 0,
      refetchInterval: REFETCH_INTERVAL_MS,
    },
  )
}

export default useStationErrorLogs
